import type { CSSProperties, ReactNode } from 'react'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { providerRegistry } from './providerRegistry'
import type { ElementProviderMappings } from '../../../types/schema'
import { Button, StatusChip } from '../../Common'
import './ExternalLinkSidecard.css'

export type ExternalLinkSupportedProvider = 'NOTION' | 'JIRA' | 'GOOGLE_CALENDAR' | 'ZOOM' | 'GITHUB' | 'FIGMA'

export type ExternalLinkSidecardItem = {
  id: string
  type: 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST_ITEM' | 'MEETING'
  providerMappings?: ElementProviderMappings
}

export type ExternalLinkSidecardProps = {
  item: ExternalLinkSidecardItem
  providers: string[]
  mode: 'sidecar' | 'table' | 'inline'
  disabled?: boolean
  showSyncActions?: boolean
  linkedOnly?: boolean
  emptyLabel?: string
  onPush?: (providerId: ExternalLinkSupportedProvider) => void
  onPull?: (providerId: ExternalLinkSupportedProvider) => void
  onResolveConflict?: (providerId: ExternalLinkSupportedProvider) => void
  children?: ReactNode
}

const resolveExternalLinkUrl = (providerId: string, externalId: string): string => {
  if (providerId === 'GOOGLE_CALENDAR') {
    return `https://calendar.google.com/calendar/u/0/r/eventedit/${encodeURIComponent(externalId)}`
  }
  if (providerId === 'JIRA') {
    return externalId.startsWith('http') ? externalId : `https://jira.atlassian.net/browse/${externalId}`
  }
  if (providerId === 'ZOOM') {
    return `https://zoom.us/j/${encodeURIComponent(externalId)}`
  }
  if (providerId === 'GITHUB') return externalId.startsWith('http') ? externalId : `https://github.com/${externalId}`
  if (providerId === 'FIGMA') return externalId.startsWith('http') ? externalId : `https://www.figma.com/file/${externalId}`
  return `https://www.notion.so/${externalId.replace(/-/g, '')}`
}

export const ExternalLinkSidecard = ({
  item,
  providers,
  mode,
  disabled = false,
  showSyncActions = true,
  linkedOnly = false,
  emptyLabel,
  onPush,
  onPull,
  onResolveConflict,
  children,
}: ExternalLinkSidecardProps) => {
  const rows = providers
    .map((providerId) => providerRegistry[providerId as keyof typeof providerRegistry])
    .filter((provider): provider is NonNullable<typeof provider> => {
      if (!provider || !provider.entityTypes.includes(item.type)) return false
      if (!linkedOnly) return true
      const mappings = Object.values(item.providerMappings ?? {}).filter(
        (mapping) => mapping.provider === provider.providerId,
      )
      const target = mappings.flatMap((mapping) => Object.values(mapping.targets))[0]
      return Boolean(target?.externalId)
    })

  return (
    <aside
      className={`externalLinkSidecard externalLinkSidecard--${mode}`}
      data-item-id={item.id}
      data-provider-count={rows.length}
      aria-label="External links"
    >
      {children ??
        (rows.length ? (
          rows.map((provider) => {
            const mappings = Object.values(item.providerMappings ?? {}).filter(
              (mapping) => mapping.provider === provider.providerId,
            )
            const conflict = mappings.some((mapping) =>
              Object.values(mapping.targets).some((target) => Boolean(target.conflictId)),
            )
            const target = mappings.flatMap((mapping) => Object.values(mapping.targets))[0]
            const externalId = target?.externalId
            const externalUrl = typeof (target as { url?: unknown } | undefined)?.url === 'string'
              ? (target as unknown as { url: string }).url
              : undefined
            const hasActions = Boolean(showSyncActions || externalId)

            if (mode === 'inline') {
              return (
                <section
                  key={provider.providerId}
                  className="externalLinkSidecard__provider"
                  style={{ '--external-link-color': provider.color } as CSSProperties}
                >
                  <div className="externalLinkSidecard__titleGroup">
                    <LinkRoundedIcon fontSize="small" />
                    <strong>{provider.label}</strong>
                  </div>
                  {conflict && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onResolveConflict?.(provider.providerId as ExternalLinkSupportedProvider)}
                    >
                      <WarningAmberRoundedIcon fontSize="inherit" />
                      <span>Conflict detected</span>
                    </Button>
                  )}
                  <div className="externalLinkSidecard__rightCluster">
                    {externalId ? (
                      <StatusChip size="sm" tone="success">Linked</StatusChip>
                    ) : (
                      <StatusChip size="sm" tone="muted">Not linked</StatusChip>
                    )}
                    {hasActions && (
                      <div className="externalLinkSidecard__actions">
                        {showSyncActions && (
                          <>
                            <Button
                              variant="tertiary"
                              size="sm"
                              disabled={disabled}
                              onClick={() => onPush?.(provider.providerId as ExternalLinkSupportedProvider)}
                            >
                              <SyncRoundedIcon fontSize="inherit" />
                              <span>Push</span>
                            </Button>
                            <Button
                              variant="tertiary"
                              size="sm"
                              disabled={disabled}
                              onClick={() => onPull?.(provider.providerId as ExternalLinkSupportedProvider)}
                            >
                              <span>Pull</span>
                            </Button>
                          </>
                        )}
                        {externalId && (
                          <Button
                            variant="tertiary"
                            size="sm"
                            onClick={() =>
                              window.open(
                                resolveExternalLinkUrl(provider.providerId, externalUrl ?? externalId),
                                '_blank',
                                'noopener,noreferrer',
                              )
                            }
                            aria-label={`Open in ${provider.label}`}
                          >
                            <OpenInNewRoundedIcon fontSize="inherit" />
                            <span>Open</span>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </section>
              )
            }

            return (
              <section
                key={provider.providerId}
                className="externalLinkSidecard__provider"
                style={{ '--external-link-color': provider.color } as CSSProperties}
              >
                <header className="externalLinkSidecard__header">
                  <div className="externalLinkSidecard__titleGroup">
                    <LinkRoundedIcon fontSize="small" />
                    <strong>{provider.label}</strong>
                  </div>
                  {externalId ? (
                    <StatusChip size="sm" tone="success">Linked</StatusChip>
                  ) : (
                    <StatusChip size="sm" tone="muted">Not linked</StatusChip>
                  )}
                </header>
                {conflict && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onResolveConflict?.(provider.providerId as ExternalLinkSupportedProvider)}
                  >
                    <WarningAmberRoundedIcon fontSize="inherit" />
                    <span>Conflict detected</span>
                  </Button>
                )}
                {hasActions && (
                  <div className="externalLinkSidecard__actions">
                    {showSyncActions && (
                      <>
                        <Button
                          variant="tertiary"
                          size="sm"
                          disabled={disabled}
                          onClick={() => onPush?.(provider.providerId as ExternalLinkSupportedProvider)}
                        >
                          <SyncRoundedIcon fontSize="inherit" />
                          <span>Push</span>
                        </Button>
                        <Button
                          variant="tertiary"
                          size="sm"
                          disabled={disabled}
                          onClick={() => onPull?.(provider.providerId as ExternalLinkSupportedProvider)}
                        >
                          <span>Pull</span>
                        </Button>
                      </>
                    )}
                    {externalId && (
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() =>
                          window.open(
                            resolveExternalLinkUrl(provider.providerId, externalUrl ?? externalId),
                            '_blank',
                            'noopener,noreferrer',
                          )
                        }
                        aria-label={`Open in ${provider.label}`}
                      >
                        <OpenInNewRoundedIcon fontSize="inherit" />
                        <span>Open</span>
                      </Button>
                    )}
                  </div>
                )}
              </section>
            )
          })
        ) : (
          <span className="externalLinkSidecard__empty">
            {emptyLabel ??
              `Connect ${providers.map((id) => providerRegistry[id as keyof typeof providerRegistry]?.label ?? id).join(' or ')} in Link Hub to add external links.`}
          </span>
        ))}
    </aside>
  )
}
